// Global Instructions Rule Applied!
//
// LP Allocation grid (LP agents UAT follow-ups PR E) on the canonical
// TableView: columns from cfg.reporting_columns (LP link, LP status, Agent
// drafts, Funds, Closed commitments, Prospects), then one editable column per
// account-ownership title (cfg.contact_relationship_titles — data-driven,
// not reporting cfg, because the roles are themselves cfg). Custom cells:
// the LP name opens the account form, Agent drafts is the per-LP toggle,
// each role column is an inline Select. Row selection feeds the bulk bar.
import React, { useMemo } from 'react'
import { Select } from 'antd'
import type { TableProps } from 'antd'
import TableView from '../../../core/components/TableView/TableView.tsx'
import { createTableColumns } from '../../../ui_configs/table_grid_columns.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import type { ViewConfigColumn } from '../../../core/components/ViewConfig/types.ts'
import type { FilterValue, SortConfig } from '../../global_configs/tableViewPreferences.ts'
import { LpAllocationRow } from '../../../state_manager/data_models/relationships_model.ts'
import { AccountOwnerRoleModel } from '../../../state_manager/data_models/account_owner_role_model.ts'
import { AgentDraftsToggle } from '../../internal_agents/components/AgentDraftsToggle.tsx'
import type { FilterOptions, FilterConfig } from '../../../core/components/TableView/TableViewTypes.ts'

type GridRow = LpAllocationRow & { id: string; key: string }

interface LpAllocationTableProps {
  data: LpAllocationRow[]
  loading: boolean
  loadingMore?: boolean
  hasMore?: boolean
  currentPage?: number
  gridColumns: ViewConfigColumn[]
  roles: AccountOwnerRoleModel[]
  userOptions: Array<{ value: string; label: string }>
  activeFilters?: FilterValue[]
  activeSorting?: SortConfig[]
  filterOptions?: FilterOptions
  filterOptionsLoading?: boolean
  busyKey: string | null
  onLoadMore?: (page: number, pageSize: number) => void | Promise<void>
  onFiltersChange?: (filters: FilterConfig) => void
  onSortChange?: (sortConfig: any) => void
  onDraftsChange: (row: LpAllocationRow, next: boolean | null) => void
  onAssignOwner: (row: LpAllocationRow, role: AccountOwnerRoleModel, userIds: string[]) => void
  rowSelection?: TableProps<GridRow>['rowSelection']
}

export const LpAllocationTable = React.memo(function LpAllocationTable({
  data,
  loading,
  loadingMore = false,
  hasMore = false,
  currentPage = 1,
  gridColumns,
  roles,
  userOptions,
  activeFilters = [],
  activeSorting = [],
  filterOptions = {},
  filterOptionsLoading = false,
  busyKey,
  onLoadMore,
  onFiltersChange,
  onSortChange,
  onDraftsChange,
  onAssignOwner,
  rowSelection
}: LpAllocationTableProps) {
  const { openFormInTab } = useTabManagerContext()

  const { columns: baseColumns, frozenColumns } = useMemo(
    () => createTableColumns({ columns: gridColumns, fallbackRecordModuleKey: 'lp_allocation' }),
    [gridColumns]
  )

  const columns = useMemo(() => {
    const cfgColumns = baseColumns.map((col) => {
      const key = String(col.key)
      if (key === 'accounts__name') {
        return {
          ...col,
          render: (value: unknown, record: GridRow) => (
            <button
              type='button'
              className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
              onClick={() => openFormInTab('accounts', record.accounts__id, typeof value === 'string' ? value : undefined)}
            >
              {typeof value === 'string' && value ? value : record.accounts__name}
            </button>
          )
        }
      }
      if (key === 'prog__agent_drafts') {
        return {
          ...col,
          render: (_: unknown, record: GridRow) => (
            <AgentDraftsToggle
              value={record.accounts__agent_drafts_enabled ?? null}
              disabled={busyKey === `${record.accounts__id}:drafts`}
              onChange={(next) => onDraftsChange(record, next)}
            />
          )
        }
      }
      return col
    })
    const roleColumns = roles.map((role) => ({
      title: role.label,
      key: `role:${role.role_key}`,
      dataIndex: `role:${role.role_key}`,
      width: 220,
      render: (_: unknown, record: GridRow) => {
        const holders = record.prog__owners[role.role_key] ?? []
        const value = holders.map((h) => h.system_users__id).filter((id): id is string => Boolean(id))
        // Phase 8: a holder whose login is gone still owns the account; the
        // picker cannot show them, so name them beneath it.
        const departed = holders
          .filter((h) => !h.system_users__id)
          .map((h) => h.contacts__name || h.system_users__name)
          .filter(Boolean)
        const key = `${record.accounts__id}:${role.id}`
        return (
          <span className='block'>
            <Select
              size='small'
              className='w-full'
              mode={role.is_exclusive ? undefined : 'multiple'}
              allowClear
              showSearch
              optionFilterProp='label'
              placeholder={role.is_exclusive ? 'Unassigned' : 'Add…'}
              options={userOptions}
              value={role.is_exclusive ? (value[0] ?? undefined) : value}
              loading={busyKey === key}
              disabled={busyKey === key}
              onChange={(v: string | string[] | undefined) =>
                onAssignOwner(record, role, v === undefined || v === null ? [] : Array.isArray(v) ? v : [v])
              }
            />
            {departed.length > 0 ? (
              <span className='block text-[11px] text-gray-400 mt-0.5'>{departed.join(', ')} (no login)</span>
            ) : null}
          </span>
        )
      }
    }))
    return [...cfgColumns, ...roleColumns]
  }, [baseColumns, roles, userOptions, busyKey, onDraftsChange, onAssignOwner, openFormInTab])

  const dataSource = useMemo<GridRow[]>(
    () => (data ?? []).map((row) => ({ ...row, id: row.accounts__id, key: row.accounts__id })),
    [data]
  )

  const currentFilters = useMemo(() => {
    if (!Array.isArray(activeFilters)) return activeFilters ?? {}
    return activeFilters.reduce((acc: Record<string, any>, f) => {
      acc[f.key] = f.value
      return acc
    }, {})
  }, [activeFilters])

  const currentSortConfig = useMemo(() => {
    if (!Array.isArray(activeSorting) || activeSorting.length === 0) return null
    const first = activeSorting[0]
    return { column: first.key, direction: first.direction }
  }, [activeSorting])

  if (columns.length === 0) return null

  return (
    <div className='flex-1 min-h-0 h-full w-full'>
      <TableView<GridRow>
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        currentPage={currentPage}
        onLoadMore={onLoadMore}
        currentFilters={currentFilters}
        onFiltersChange={onFiltersChange}
        onSortChange={onSortChange}
        filterOptions={filterOptions}
        filterOptionsLoading={filterOptionsLoading}
        currentSortConfig={currentSortConfig}
        frozenColumns={frozenColumns}
        rowSelection={rowSelection}
        headerStyle={{ padding: '5px', minHeight: 40, backgroundColor: '#f5f5f5' }}
      />
    </div>
  )
})

export default LpAllocationTable
